import { collection, query, where, getDocs } from 'firebase/firestore';
import Hashids from 'hashids';
import { db } from '../../firebase/config';
import { getUserId } from '../../shared/utils/user-details';

export function hashUserId(userId: string | number): string {
  const secretKey = process.env.REACT_APP_CFG_MISC_09;

  if (!userId) {
    throw new Error('User ID is required for hashing');
  }

  // Convert userId to number for Hashids
  const userIdNumber =
    typeof userId === 'string' ? parseInt(userId, 10) : userId;

  // Create Hashids instance with secret salt
  const hashids = new Hashids(secretKey, 10);

  // Encode the user ID
  const hashId = hashids.encode(userIdNumber);

  return hashId;
}

export function decodeHashedUserId(hashedId: string): number {
  const secretKey = process.env.REACT_APP_CFG_MISC_09;

  if (!hashedId) {
    throw new Error('Hashed ID is required for decoding');
  }

  // Create Hashids instance with same secret salt and settings
  const hashids = new Hashids(secretKey, 10);

  // Decode the hashed ID
  const decoded = hashids.decode(hashedId);

  if (!decoded || decoded.length === 0) {
    throw new Error('Invalid hashed ID or decoding failed');
  }

  // Return the first decoded number
  return decoded[0] as number;
}

/**
 * Validates if the current user's tracking ID has access to a specific document
 * WITHOUT fetching ANY data using Firebase queries
 * @param collectionName - The collection name
 * @param documentId - The document ID to check access for
 * @returns Promise<boolean> - true if access is granted, false otherwise
 */
export async function validateTrackingId(
  collectionName: string,
  documentId?: string,
): Promise<boolean> {
  try {
    const currentUserId = getUserId();

    if (!currentUserId) {
      throw new Error('User ID not found');
    }

    // Hash the user ID using the secret key
    const hashedUserId = hashUserId(currentUserId);

    const q = query(
      collection(db, collectionName),
      where('__name__', '==', documentId),
      where('accessibleUserIds', 'array-contains', hashedUserId),
    );

    const querySnapshot = await getDocs(q);

    // If query returns any documents, the document exists and access is granted
    if (!querySnapshot.empty) {
      return true;
    }
    // No documents returned means the document doesn't exist, access is denied
    throw new Error("Document doesn't exists");
  } catch (error: any) {
    console.error(error.message);
    return false;
  }
}
